import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import './PracticeProfileImages.scss';
import { UploadPracticeProfileImage } from '../../slices/profiles-data-slice/thunks/upload-practice-profile-image.thunk';
import { RemovePracticeProfileImage } from '../../slices/profiles-data-slice/thunks/remove-practice-profile-image.thunk';
import { PracticeProfileImage } from '../../slices/profiles-data-slice/models/practice-profile-image';

interface PracticeProfileImagesProps {
    images: PracticeProfileImage[];
    profileId: string;
    practiceProfileId: string;
    maxImages?: number;
    disabled?: boolean;
    className?: string;
}

const PracticeProfileImages: React.FC<PracticeProfileImagesProps> = ({
    images = [],
    profileId,
    practiceProfileId,
    maxImages = 10,
    disabled = false,
    className = '',
}) => {
    const dispatch = useDispatch() as any;
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);

    const handleFileSelect = (files: FileList | null) => {
        if (!files || disabled) return;

        const validFiles: File[] = [];
        const imageExtensions = [
            'jpg',
            'jpeg',
            'png',
            'gif',
            'bmp',
            'webp',
            'svg',
        ];

        Array.from(files).forEach((file) => {
            const extension = file.name.split('.').pop()?.toLowerCase();
            if (extension && imageExtensions.includes(extension)) {
                validFiles.push(file);
            }
        });

        if (validFiles.length === 0) {
            alert(
                'Please select valid image files (jpg, jpeg, png, gif, bmp, webp, svg)'
            );
            return;
        }

        if (images.length + validFiles.length > maxImages) {
            alert(
                `Maximum ${maxImages} images allowed. You can add ${
                    maxImages - images.length
                } more images.`
            );
            return;
        }
        dispatch(
            UploadPracticeProfileImage({
                profileId: profileId,
                practiceProfileId: practiceProfileId,
                files: validFiles,
            })
        );
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleFileSelect(e.target.files);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveImage = (image: PracticeProfileImage) => {
        if (disabled || !image.id) return;

        dispatch(
            RemovePracticeProfileImage({
                profileId,
                practiceProfileId,
                uploadId: image.id,
            })
        );
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        if (!disabled) {
            setDragOver(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        if (!disabled) {
            handleFileSelect(e.dataTransfer.files);
        }
    };

    const handleUploadClick = () => {
        if (!disabled && fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    return (
        <div
            className={`image-management ${className} ${
                disabled ? 'disabled' : ''
            }`}>
            <div
                className={`upload-area ${dragOver ? 'drag-over' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleUploadClick}>
                <div className="upload-content">
                    <p className="upload-text">
                        {dragOver
                            ? 'Drop images here'
                            : 'Click to upload or drag and drop images'}
                    </p>
                </div>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.svg,.JPG,.JPEG,.PNG,.GIF,.BMP,.WEBP,.SVG"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
            />

            {images.length > 0 && (
                <div className="images-grid">
                    {images.map((image, index) => (
                        <div key={image.id || index} className="image-item">
                            <img
                                src={image.source}
                                alt={
                                    image.name || `Practice image ${index + 1}`
                                }
                            />
                            {!disabled && image.id && (
                                <button
                                    className="remove-button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemoveImage(image);
                                    }}
                                    type="button"></button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default PracticeProfileImages;
